import { LocalNotifications, LocalNotificationSchema } from '@capacitor/local-notifications';
import { f7 } from 'framework7-svelte';
import Framework7 from 'framework7/lite/bundle';
import translate, { getTranslationValue } from '../js/i18n.ts';
import { getStoredValue, setStoredValue, StoreValue } from '../js/settings.ts';
import { Event, recordEvent } from './analytics.ts';
import { device, logAndShowError, logError } from './app.ts';
import { ANDROID_REMINDER_SOUND } from './media.ts';
import { isWebNotificationSupported as hasWebNotificationSupport, scheduleWebNotifications } from './notifications-web.ts';
import { ROUTE_HOME } from './routes.ts';
import { PersistentRandomStringGenerator } from './utils.ts';

export const ICON_SMALL = 'ic_notification';
export const ICON_LARGE = 'ic_notification_large';

const ANDROID_NOTIFICATION_CHANNEL_ID = 'com.genux.notification.reminder';

/** notification ID used when a timer is finished*/
const TIMER_NOTIF_ID = 5;

const GRANTED = 'granted'

export interface NotificationTime {
    hour: number,
    minute: number
}

function hasNativeNotificationSupport(): boolean {
    return (device.native && typeof LocalNotifications !== 'undefined');
}

export function getStoredNotificationTimes(): NotificationTime[] {
    const defaultNotifications = [
        { hour: 8, minute: 0 },
        { hour: 13, minute: 0 },
        { hour: 18, minute: 0 },
    ]

    return getStoredValue(StoreValue.notifications, defaultNotifications);
}

/**
 * Stores the notifications settings and re-schedule notifications
 * 
 * @param notifs a array of notifications to schedule
 */
export function setStoredNotificationTimes(notifs: NotificationTime[], scheduleNow: boolean = true) {
    // sanitize array
    const cleanNotifs: NotificationTime[] = Array.from(notifs, (n) => {
        return { hour: n.hour, minute: n.minute }
    });
    // sort notifications by hour
    cleanNotifs.sort(function (a, b) { return a.hour - b.hour });
    // TODO: sort by hour/time
    setStoredValue(StoreValue.notifications, cleanNotifs);
    // re-schedule notifications
    if (scheduleNow) {
        scheduleNotifications(f7, true); // cancel if there's no notifications saved
    }
}

/**
 * 
 * @returns a unique random cool sentence for notifications
 */
export function getNextUniqueMessage(): string {
    const notificationMessages = getTranslationValue('messages.notifications') as [] ?? [];
    const uniqueRandomMessageGenerator = new PersistentRandomStringGenerator('notifications', notificationMessages);
    return uniqueRandomMessageGenerator.getNext();
}

async function scheduleStoredNotifications(notifTimes: NotificationTime[]) {
    const schedules: LocalNotificationSchema[] = [];
    const START_ID = 10;
    // cancel all notifications first
    try {
        await cancelAll()
    } catch (err) {
        logError("notifications:cancelAll caught error", err)
    }
    // create notifications
    for (const ntime of notifTimes) {
        const title = getNextUniqueMessage(); // different sentence every time
        const message = translate("notification.subtitle");
        // add to the scheduled notifications
        schedules.push({
            id: START_ID + schedules.length,
            title: title, // TODO remove split and do separate trs
            body: message,
            channelId: ANDROID_NOTIFICATION_CHANNEL_ID,
            // smallIcon: ICON_SMALL, set in capacitor.config.js:LocalNotifications
            schedule: {
                on: {
                    hour: ntime.hour,
                    minute: ntime.minute,
                    second: 0
                },
                allowWhileIdle: true
            }
        })
    }
    // schedule
    try {
        if (schedules.length) {
            await LocalNotifications.schedule({
                notifications: schedules
            });
        }
        console.debug(`notifications: scheduled count=${schedules.length}`, schedules)
        return schedules;

    } catch (err) {
        logError("notifications:schedule caught error", err)
    }
}

async function cancelAll() {
    const pendingNotifications = await LocalNotifications.getPending()
    const count = pendingNotifications?.notifications?.length || 0
    if (count > 0) {
        await LocalNotifications.cancel({
            notifications: pendingNotifications.notifications
        })
    }
    console.debug(`notifications: cancelled count=${count}`)
}

async function scheduleNativeNotifications(notifTimes: NotificationTime[]) {
    try {
        if (device.android) { // allow to set custom sounds on android
            await LocalNotifications.createChannel({
                id: ANDROID_NOTIFICATION_CHANNEL_ID,
                name: 'Daily Reminders',
                description: 'Daily Reminders',
                sound: ANDROID_REMINDER_SOUND,
            });

            // hack to tweak the channel name of plugin-background
            await LocalNotifications.createChannel({
                id: 'cordova-plugin-background-mode-id',
                name: 'Ongoing Session',
                description: 'Ongoing Session',
            });
        }

        let res = await LocalNotifications.checkPermissions();
        if (res.display === GRANTED) {
            return await scheduleStoredNotifications(notifTimes) // schedule
        } else { // no permissions, request them
            try {
                // ask 3 times
                for (let requestedCount = 0; requestedCount < 3; requestedCount++) {
                    res = await LocalNotifications.requestPermissions();
                    if (res.display === GRANTED) {
                        return await scheduleStoredNotifications(notifTimes) // schedule
                    } else {
                        console.warn(`notifications: user did not grant permissions count=${requestedCount} data=${JSON.stringify(res)}`);
                    }
                }
            }
            catch (err) {
                logError('notifications:requestPermissions caught an error', err)
            }
        }
    }
    catch (err) {
        logError('notifications:checkPermissions caught an error', err)
    }
}

export async function scheduleNotifications(f7: Framework7, scheduleWhenEmpty: boolean = false) {
    const notifTimes = getStoredNotificationTimes();
    if (notifTimes.length === 0 && !scheduleWhenEmpty) {
        console.debug(`notifications: no notifications to be scheduled`);
        return;
    }

    if (hasWebNotificationSupport()) {
        console.debug(`notifications: Scheduling web notifications ${notifTimes.length}`);
        scheduleWebNotifications(notifTimes, f7); // no await() here

    } else if (hasNativeNotificationSupport()) {
        console.debug(`notifications: Scheduling native notifications ${notifTimes.length}`);
        await scheduleNativeNotifications(notifTimes);

    } else {
        console.warn('notifications: Notifications are not supported on this device');
    }
}

export const hideSessionNotification = async () => {
    if (!hasNativeNotificationSupport()) {
        console.warn('notifications:hideSessionNotification No notifications plugin available, notifications disabled');
        return;
    }

    await LocalNotifications.removeAllDeliveredNotifications()
    console.debug(`notifications:hideSessionNotification`);
}

export const onNotificationDisplayed = (f7: Framework7, notification: LocalNotificationSchema) => {
    const nowDate = new Date();
    console.debug('notifications:onNotificationDisplayed', notification);
    if (notification.id === TIMER_NOTIF_ID) {
        // could send eevent here
        const scheduledAt = notification.schedule?.at;
        if (scheduledAt) {
            console.debug(`notifications:onNotificationDisplayed time difference=${nowDate.getTime() - scheduledAt.getTime()}ms`);
        }
    }
}

export const onNotificationClick = (f7: Framework7) => {
    console.debug('notifications:onNotificationClick');
    recordEvent(Event.PageView, { page_location: '/notifications/scheduled' });
    // start coherence session immediately
    const router = f7.views.main?.router; // needs the view's router instance to work
    if (router) {
        router.navigate(ROUTE_HOME, {
            reloadCurrent: true, // force refresh
            ignoreCache: true, // force refresh
            clearPreviousHistory: true, // clear history
            history: false, // don't add this route to history
            browserHistory: false, // don't add this route to history
            props: {
                notui: true, // indicate user did not interact with screen (no click event)
            },
        });
    } else {
        logAndShowError('Local notification clicked but no view/router available');
    }
}

/**
 * Setup notifications for application
 * 
 * @param f7 the Framework7 app instance
 */
export const setupNotifications = async (f7: Framework7) => {
    console.debug('notifications: setting up notifications');
    // native apps
    if (hasNativeNotificationSupport()) {
        // click notification listener
        await LocalNotifications.addListener('localNotificationActionPerformed', () => onNotificationClick(f7));
        await LocalNotifications.addListener('localNotificationReceived', (notification: LocalNotificationSchema) => onNotificationDisplayed(f7, notification));
    }

    // check permissions and schedule notifications
    await scheduleNotifications(f7, false); // not scheduling (cancelling) empty notifications
};

/**
 * Background functions to keep the app running in the background
 */


/**
 * Starts/Stops the foreground service that keeps the application running in the background
 */
/* eslint-disable @typescript-eslint/no-explicit-any */
declare const cordova: any;

class BackgroundPlugin {

    private bgPlugin;

    constructor() {
        this.bgPlugin = device.native && typeof cordova !== 'undefined' && cordova?.plugins?.backgroundMode;
        if (!this.bgPlugin) {
            console.warn('backgroundmode: plugin not available');
            return;
        }

        this.bgPlugin.setDefaults({
            title: 'Coherence',
            text: translate('messages.notification_ongoing_session'),
            icon: ICON_SMALL,
            color: '8A2BE2', // should match iconColor in capacitor.config.ts
            hidden: false, // make it stick and show on lock screen
            silent: false // make it stick and show on lock screen
        });

        // listen for changes
        this.bgPlugin.on('activate', () => {
            console.debug('backgroundmode: activated. disabling webview optimizations');
            this.bgPlugin.disableWebViewOptimizations(); // keep javascript running in the background
        });
        this.bgPlugin.on('deactivate', function () {
            console.debug('backgroundmode: deactivated');
            // nothing to do here
        });
    }


    /**
     * Starts the foreground service that keeps the application running in the background
     */
    public keepRunningBackground = () => {
        if (!this.bgPlugin) {
            return;
        }
        // start background mode
        console.debug('backgroundmode: enabling');
        this.bgPlugin.setEnabled(true);
    }

    /**
   * Stops the foreground service that keeps the application running in the background
   */
    public stopRunningBackground = () => {
        if (!this.bgPlugin) {
            return;
        }
        // deactivate background mode
        console.debug('backgroundmode: disabling');
        this.bgPlugin.setEnabled(false);
    }

}

// delayed init until called or it can error
let backgroundPlugin: BackgroundPlugin;

/**
 * Stops the background process by invoking the `stopRunningBackground` method
 * on the `backgroundPlugin` instance.
 *
 */
export const stopAppRunningInBackground = () => {
    if (!backgroundPlugin) {
        backgroundPlugin = new BackgroundPlugin();
    }
    backgroundPlugin.stopRunningBackground();
}

export const keepAppRunningInBackground = () => {
    if (!backgroundPlugin) {
        backgroundPlugin = new BackgroundPlugin();
    }
    backgroundPlugin.keepRunningBackground();
}